/**
 * Flatten SVG path transforms so all path coordinates are in the root SVG (viewBox) space.
 * Warp.js reads path 'd' in each path's local coordinate system; if paths live inside
 * <g transform='...'>, black/white (or different groups) deform inconsistently because
 * the warp mesh is in viewBox space. This flattens all path data into viewBox space and
 * moves paths into a single root group so the warp deforms black and white together.
 */

const CMD_REGEX = /([MmZzLlHhVvCcSsQqTtAa])([^MmZzLlHhVvCcSsQqTtAa]*)/g;
const NUM_REGEX = /-?[0-9]*\.?[0-9]+(?:e[-+]?\d+)?/gi;

function parseNumbers(str) {
  const nums = [];
  let m;
  NUM_REGEX.lastIndex = 0;
  while ((m = NUM_REGEX.exec(str)) !== null) nums.push(parseFloat(m[0]));
  return nums;
}

/**
 * Parse path 'd' into segments with absolute coordinates.
 * Each segment: { type, values: [x,y,...] }. Tracks current point for H/V and relative.
 */
function parsePathToAbsoluteSegments(d) {
  if (!d || typeof d !== "string") return [];
  const segments = [];
  let curX = 0;
  let curY = 0;
  let startX = 0;
  let startY = 0;
  let match;
  CMD_REGEX.lastIndex = 0;
  while ((match = CMD_REGEX.exec(d)) !== null) {
    const cmd = match[1];
    const raw = match[2].trim();
    const n = parseNumbers(raw);
    const upper = cmd.toUpperCase();
    const rel = cmd === cmd.toLowerCase() && cmd !== "z";

    if (cmd === "z" || cmd === "Z") {
      segments.push({ type: "Z", values: [] });
      curX = startX;
      curY = startY;
      continue;
    }

    if (upper === "M") {
      for (let i = 0; i < n.length; i += 2) {
        if (rel) {
          curX += n[i];
          curY += n[i + 1];
        } else {
          curX = n[i];
          curY = n[i + 1];
        }
        segments.push({ type: i === 0 ? "M" : "L", values: [curX, curY] });
      }
      startX = curX;
      startY = curY;
      continue;
    }

    if (upper === "L") {
      for (let i = 0; i < n.length; i += 2) {
        if (rel) {
          curX += n[i];
          curY += n[i + 1];
        } else {
          curX = n[i];
          curY = n[i + 1];
        }
        segments.push({ type: "L", values: [curX, curY] });
      }
      continue;
    }

    if (upper === "H") {
      for (let i = 0; i < n.length; i++) {
        if (rel) curX += n[i];
        else curX = n[i];
        segments.push({ type: "L", values: [curX, curY] });
      }
      continue;
    }

    if (upper === "V") {
      for (let i = 0; i < n.length; i++) {
        if (rel) curY += n[i];
        else curY = n[i];
        segments.push({ type: "L", values: [curX, curY] });
      }
      continue;
    }

    if (upper === "C") {
      for (let i = 0; i < n.length; i += 6) {
        let x1 = n[i],
          y1 = n[i + 1],
          x2 = n[i + 2],
          y2 = n[i + 3],
          x = n[i + 4],
          y = n[i + 5];
        if (rel) {
          x1 += curX;
          y1 += curY;
          x2 += curX;
          y2 += curY;
          x += curX;
          y += curY;
        }
        curX = x;
        curY = y;
        segments.push({ type: "C", values: [x1, y1, x2, y2, x, y] });
      }
      continue;
    }

    if (upper === "S") {
      const prev = segments[segments.length - 1];
      let ctrl1X = curX,
        ctrl1Y = curY;
      if (prev && prev.type === "C" && prev.values.length >= 6) {
        ctrl1X = 2 * curX - prev.values[2];
        ctrl1Y = 2 * curY - prev.values[3];
      }
      for (let i = 0; i < n.length; i += 4) {
        let x2 = n[i],
          y2 = n[i + 1],
          x = n[i + 2],
          y = n[i + 3];
        if (rel) {
          x2 += curX;
          y2 += curY;
          x += curX;
          y += curY;
        }
        segments.push({ type: "C", values: [ctrl1X, ctrl1Y, x2, y2, x, y] });
        curX = x;
        curY = y;
        ctrl1X = 2 * x - x2;
        ctrl1Y = 2 * y - y2;
      }
      continue;
    }

    if (upper === "Q") {
      for (let i = 0; i < n.length; i += 4) {
        let x1 = n[i],
          y1 = n[i + 1],
          x = n[i + 2],
          y = n[i + 3];
        if (rel) {
          x1 += curX;
          y1 += curY;
          x += curX;
          y += curY;
        }
        curX = x;
        curY = y;
        segments.push({ type: "Q", values: [x1, y1, x, y] });
      }
      continue;
    }

    if (upper === "T") {
      for (let i = 0; i < n.length; i += 2) {
        let x = n[i],
          y = n[i + 1];
        if (rel) {
          x += curX;
          y += curY;
        }
        curX = x;
        curY = y;
        segments.push({ type: "Q", values: [curX, curY, x, y] });
      }
      continue;
    }

    if (upper === "A") {
      for (let i = 0; i < n.length; i += 7) {
        const rx = n[i],
          ry = n[i + 1],
          xRot = n[i + 2],
          la = n[i + 3],
          sweep = n[i + 4];
        let x = n[i + 5],
          y = n[i + 6];
        if (rel) {
          x += curX;
          y += curY;
        }
        curX = x;
        curY = y;
        segments.push({ type: "A", values: [rx, ry, xRot, la, sweep, x, y] });
      }
      continue;
    }
  }
  return segments;
}

function transformPoint(svg, x, y, matrix) {
  const pt = svg.createSVGPoint();
  pt.x = x;
  pt.y = y;
  const t = pt.matrixTransform(matrix);
  return [t.x, t.y];
}

function transformSegment(svg, segment, matrix) {
  const vals = segment.values;
  const out = [];
  const pairCount = { M: 1, L: 1, C: 3, Q: 2, A: 1 }; // A: only transform endpoint (index 5,6)
  const count = pairCount[segment.type] || 1;
  if (segment.type === "A") {
    const [rx, ry, xRot, la, sweep, x, y] = vals;
    const [nx, ny] = transformPoint(svg, x, y, matrix);
    return { ...segment, values: [rx, ry, xRot, la, sweep, nx, ny] };
  }
  if (segment.type === "Z") return segment;
  for (let i = 0; i < vals.length; i += 2) {
    const [nx, ny] = transformPoint(svg, vals[i], vals[i + 1], matrix);
    out.push(nx, ny);
  }
  return { ...segment, values: out };
}

function encodeSegments(segments, precision = 4) {
  const round = (v) => Math.round(v * 10 ** precision) / 10 ** precision;
  const parts = [];
  for (const s of segments) {
    if (s.type === "Z") {
      parts.push("Z");
      continue;
    }
    const v = s.values.map(round);
    if (s.type === "M") parts.push(`M ${v[0]} ${v[1]}`);
    else if (s.type === "L") parts.push(`L ${v[0]} ${v[1]}`);
    else if (s.type === "C")
      parts.push(`C ${v[0]} ${v[1]} ${v[2]} ${v[3]} ${v[4]} ${v[5]}`);
    else if (s.type === "Q") parts.push(`Q ${v[0]} ${v[1]} ${v[2]} ${v[3]}`);
    else if (s.type === "A")
      parts.push(`A ${v[0]} ${v[1]} ${v[2]} ${v[3]} ${v[4]} ${v[5]} ${v[6]}`);
  }
  return parts.join(" ");
}

/**
 * Collect all path elements in document order (depth-first).
 */
function collectPathsInOrder(parent, list = []) {
  const children = parent.children || [];
  for (let i = 0; i < children.length; i++) {
    const el = children[i];
    if (el.tagName && el.tagName.toLowerCase() === "path") {
      list.push(el);
    } else {
      collectPathsInOrder(el, list);
    }
  }
  return list;
}

/**
 * Flatten all path transforms so every path's 'd' is in the root SVG (viewBox) coordinate system.
 * Replaces the SVG's content with a single <g> containing all paths in order with flattened 'd'.
 * This ensures the warp mesh (in viewBox space) deforms black and white paths consistently.
 * Preserves specified elements inside a <defs> element.
 *
 * @param {SVGElement} svgElement - The SVG element to flatten
 */
export default function flattenSvgTransforms(svgElement) {
  if (!svgElement || svgElement.tagName?.toLowerCase() !== "svg") return;

  const svgCTM = svgElement.getCTM();
  if (!svgCTM) return;

  const pathElements = collectPathsInOrder(svgElement);
  if (pathElements.length === 0) return;

  const ns = "http://www.w3.org/2000/svg";

  const preserveDefsElements = ['style'];
  // Preserve specified elements from defs
  const preservedElements = [];
  const existingDefs = svgElement.querySelector('defs');
  if (existingDefs && preserveDefsElements.length > 0) {
    preserveDefsElements.forEach(tagName => {
      const elements = existingDefs.querySelectorAll(tagName);
      elements.forEach(element => {
        preservedElements.push(element.cloneNode(true));
      });
    });
  }

  const wrapper = document.createElementNS(ns, "g");
  wrapper.setAttribute("data-flattened", "true");

  for (const pathEl of pathElements) {
    const pathCTM = pathEl.getCTM();
    if (!pathCTM) continue;

    const pathToSvg = svgCTM.inverse().multiply(pathCTM);
    const d = pathEl.getAttribute("d");
    const segments = parsePathToAbsoluteSegments(d);
    const transformed = segments.map((seg) =>
      transformSegment(svgElement, seg, pathToSvg),
    );
    const newD = encodeSegments(transformed);

    const newPath = document.createElementNS(ns, "path");
    newPath.setAttribute("d", newD);
    for (const attr of Array.from(pathEl.attributes)) {
      const name = attr.name;
      if (name === "d" || name === "transform") continue;
      newPath.setAttribute(name, attr.value);
    }
    wrapper.appendChild(newPath);
  }

  while (svgElement.firstChild) svgElement.removeChild(svgElement.firstChild);

  // Re-add defs element with preserved elements if any exist
  if (preservedElements.length > 0) {
    const newDefs = document.createElementNS(ns, "defs");
    preservedElements.forEach(element => {
      newDefs.appendChild(element);
    });
    svgElement.appendChild(newDefs);
  }

  svgElement.appendChild(wrapper);
}
